import { useCallback, useEffect, useRef, useState } from 'react'

// lib.dom ships the result types but not the recognizer itself.
type Recognition = {
    lang: string
    continuous: boolean
    interimResults: boolean
    onresult: ((e: { resultIndex: number; results: SpeechRecognitionResultList }) => void) | null
    onerror: ((e: { error: string }) => void) | null
    onaudiostart: (() => void) | null
    onend: (() => void) | null
    start: () => void
    stop: () => void
    abort: () => void
}
type RecognitionCtor = new () => Recognition

const RecognitionImpl: RecognitionCtor | undefined =
    typeof window === 'undefined'
        ? undefined
        : (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition

export const isSpeechToTextSupported = !!RecognitionImpl

const BLOCKED_ERRORS = new Set(['not-allowed', 'service-not-allowed'])
// Silence and our own stop/abort are normal endings, not failures.
const IGNORED_ERRORS = new Set(['no-speech', 'aborted'])

/** `starting` covers the gap before the mic is live; speech in it would be lost. */
export type SpeechStatus = 'idle' | 'starting' | 'listening'

export type SpeechTranscript = {
    /** Text the recognizer has settled on. */
    final: string
    /** Text still being recognized; may change or disappear. */
    interim: string
}

/**
 * Browser speech-to-text for the chat composer. Reports the transcript for the
 * current listening session through `onText` until stopped.
 */
export default function useSpeechToText(onText: (transcript: SpeechTranscript) => void) {
    const [status, setStatus] = useState<SpeechStatus>('idle')
    const [error, setError] = useState<string | null>(null)
    const recognitionRef = useRef<Recognition | null>(null)
    const onTextRef = useRef(onText)
    onTextRef.current = onText

    // Stops listening but still delivers what was already heard.
    const stop = useCallback(() => {
        recognitionRef.current?.stop()
    }, [])

    // Stops listening and drops pending results, e.g. once the message is sent.
    const cancel = useCallback(() => {
        recognitionRef.current?.abort()
    }, [])

    const start = useCallback(() => {
        if (!RecognitionImpl || recognitionRef.current) return
        const recognition = new RecognitionImpl()
        recognition.lang = 'en-IN'
        recognition.continuous = true
        recognition.interimResults = true

        recognition.onaudiostart = () => setStatus('listening')
        recognition.onresult = (e) => {
            let final = ''
            let interim = ''
            for (let i = 0; i < e.results.length; i++) {
                const result = e.results[i]
                if (result.isFinal) final += result[0].transcript
                else interim += result[0].transcript
            }
            onTextRef.current({ final: final.trim(), interim: interim.trim() })
        }
        recognition.onerror = (e) => {
            if (IGNORED_ERRORS.has(e.error)) return
            setError(BLOCKED_ERRORS.has(e.error) ? 'Microphone access blocked' : 'Voice input failed')
        }
        recognition.onend = () => {
            recognitionRef.current = null
            setStatus('idle')
        }

        recognitionRef.current = recognition
        setError(null)
        setStatus('starting')
        recognition.start()
    }, [])

    useEffect(() => () => recognitionRef.current?.abort(), [])

    return { supported: isSpeechToTextSupported, status, error, start, stop, cancel }
}
